import axios from 'axios'
import { createSelector } from 'redux-bundler'
import { tokenHeader } from '../../utils'

const getTypes = (bundleName, customTypes = {}) => {
  const capsName = bundleName.toUpperCase()
  const get = {
    list: {
      start: `FETCH_${capsName}_LIST_START`,
      succeed: `FETCH_${capsName}_LIST_SUCCEED`,
      error: `FETCH_${capsName}_LIST_ERROR`,
    },
    retrieve: {
      start: `FETCH_${capsName}_ITEM_START`,
      succeed: `FETCH_${capsName}_ITEM_SUCCEED`,
      error: `FETCH_${capsName}_ITEM_ERROR`,
    },
  }
  const post = {
    create: {
      start: `CREATE_${capsName}_ITEM_START`,
      succeed: `CREATE_${capsName}_ITEM_SUCCEED`,
      error: `CREATE_${capsName}_ITEM_ERROR`,
    },
    update: {
      start: `UPDATE_${capsName}_ITEM_START`,
      succeed: `UPDATE_${capsName}_ITEM_SUCCEED`,
      error: `UPDATE_${capsName}_ITEM_ERROR`,
    },
  }
  const deleteMethod = {
    delete: `DELETE_${capsName}_ITEM`
  }
  const cache = {
    setCachedItems: `SET_CACHED_${capsName}_ITEMS`,
  }
  const search = {
    setSearchValue: `SET_${capsName}_SEARCH_VALUE`,
    clearSearchValue: `CLEAR_${capsName}SEARCH_VALUE`,
  }
  const dialog = {
    openDialog: `SET_${capsName}_ITEM_DIALOG_OPEN`,
    closeDialog: `SET_${capsName}_ITEM_DIALOG_CLOSED`,
    setSelectedDialogItem: `SET_${capsName}_ITEM_DIALOG_SELECTED_ITEM`,
  }
  return {
    ...get,
    ...post,
    ...cache,
    ...search,
    ...customTypes,
    ...dialog,
    ...deleteMethod,
  }
}

const getInitialState = (bundleName, extraFields, allowDelete) => ({
  ...extraFields,
  items: [],
  loading: false,
  lastFetch: null,
  lastError: null,
  lastErrorData: null,
  openViewDialog: false,
  selectedItem: null,
  searchValue: '',
  lastSearchValueSet: null,
  cachedItems: {},
  selectedItems: [],
  allSelected: false,
  allowDelete,
})

const getReducer = (bundleName, initialState, types) => {
  return (state = initialState, action) => {
    let index = null
    let newArr = null
    let itemsBuffer = {}
    switch (action.type) {
    case types.list.start:
    case types.retrieve.start:
    case types.create.start:
    case types.update.start:
      return {
        ...state,
        loading: true,
      }
    case types.list.error:
    case types.retrieve.error:
    case types.create.error:
    case types.update.error:
      return {
        ...state,
        lastFetch: Date.now(),
        loading: false,
        lastErrorData: action?.payload || null,
      }
    case types.list.succeed:
      itemsBuffer = state.cachedItems
      action.payload?.forEach(item => itemsBuffer[item.id] = item)
      return {
        ...state,
        items: action.payload,
        lastFetch: Date.now(),
        cachedItems: itemsBuffer,
        loading: false,
      }
    case types.retrieve.succeed:
    case types.update.succeed:
    case types.create.succeed:
      index = state.items.findIndex(item => item.id === action.payload.id)
      itemsBuffer = state.cachedItems
      itemsBuffer[action.payload.id] = action.payload
      if (index === -1) {
        return {
          ...state,
          cachedItems: itemsBuffer,
        }
      }
      newArr = [...state.entities]
      newArr[index] = action.payload
      return {
        ...state,
        items: newArr,
        cachedItems: itemsBuffer,
      }
    case types.setCachedItems:
      return {
        ...state,
        cachedItems: action.payload,
      }
    case types.setSelected:
      newArr = [...state.items]
      newArr.sort()
      action.payload.sort()
      return {
        ...state,
        selectedItems: action.payload,
        allSelected: newArr.length === action.payload.length,
      }
    case types.setAllSelected:
      newArr = state.items.map(item => item.id)
      return {
        ...state,
        allSelected: true,
        selectedItems: newArr,
      }
    case types.clearSelected:
      return {
        ...state,
        allSelected: false,
        selectedItems: [],
      }
    case types.setSearchValue:
      return {
        ...state,
        searchValue: action.payload,
        lastSearchValueSet: Date.now(),
      }
    case types.clearSearchValue:
      return {
        ...state,
        searchValue: '',
        lastSearchValueSet: Date.now(),
      }
    case types.openDialog:
      return {
        ...state,
        openViewDialog: true,
        selectedItem: state.cachedItems[action.payload],
      }
    case types.closeDialog:
      return {
        ...state,
        openViewDialog: false,
      }
    case types.setSelectedDialogItem:
      return {
        ...state,
        selectedItem: action.payload,
      }
    default:
      return state
    }
  }
}

const getSelectors = (bundleName, camelCaseBundleName) => {
  const selectItems = state => state[camelCaseBundleName].items
  const selectCachedItems = state => state[camelCaseBundleName].cachedItems
  const selectSearchValue = state => state[camelCaseBundleName].searchValue
  const selectRaw = state => state[camelCaseBundleName]
  const selectSelectedItems = state => state[camelCaseBundleName].selectedItems
  const selectAllSelected = state => state[camelCaseBundleName].allSelected
  const selectSelectedItem = state => state[camelCaseBundleName].selectedItem
  const selectOpenDialogView = state => state[camelCaseBundleName].openViewDialog
  const selectAllowDelete = state => state[camelCaseBundleName].allowDelete
  return {
    [`select${bundleName}ModelItems`]: selectItems,
    [`select${bundleName}CachedItems`]: selectCachedItems,
    [`select${bundleName}ModelSearchValue`]: selectSearchValue,
    [`select${bundleName}ModelRaw`]: selectRaw,
    [`select${bundleName}SelectedItems`]: selectSelectedItems,
    [`select${bundleName}AllSelected`]: selectAllSelected,
    [`select${bundleName}ModelSelectedItem`]: selectSelectedItem,
    [`select${bundleName}ModelDialogOpen`]: selectOpenDialogView,
    [`select${bundleName}ModelAllowDelete`]: selectAllowDelete,
  }
}

const getActionCreators = (bundleName, initialState, types, url, verboseName, allowDelete) => {
  const fetchList = () => async ({ dispatch, store }) => {
    dispatch({ type: types.list.start })
    const access = store.selectAccessToken()
    const search = store[`select${bundleName}ModelSearchValue`]()
    // const filterParams = store.selectFilterParams()
    const filterParams = {}
    let res
    const opts = tokenHeader(access)
    const params = {
      search,
      ...filterParams,
    }
    opts.params = params
    try {
      res = await axios.get(`/api/${url}/`, opts)
      dispatch({
        type: types.list.succeed,
        payload: res.data,
      })
    } catch (error) {
      dispatch({ type: types.list.error, payload: error?.response?.data })
      throw error
    }
  }
  const createItem = itemData => async ({ dispatch, store }) => {
    dispatch({ type: types.create.start})
    const access = store.selectAccessToken()
    const opts = tokenHeader(access)
    let res
    try {
      res = await axios.post(`/api/${url}/`, itemData, opts)
      dispatch({
        type: types.create.succeed,
        payload: res.data,
      })
      return res.data
    } catch(error) {
      store.doSetSnackbarFail(`Error al crear ${verboseName}.`)
      dispatch({
        type: types.create.error,
        payload: error?.response?.data,
      })
    }
  }
  const updateItem = itemData => async ({ dispatch, store }) => {
    dispatch({ type: types.update.start})
    const access = store.selectAccessToken()
    const opts = tokenHeader(access)
    let res
    try {
      res = await axios.post(`/api/${url}/${itemData.id}/`, itemData, opts)
      dispatch({
        type: types.update.succeed,
        payload: res.data,
      })
      return res.data
    } catch(error) {
      store.doSetSnackbarFail(`Error al guardar ${verboseName}.`)
      dispatch({
        type: types.update.error,
        payload: error?.response?.data,
      })
    }
  }
  const deleteItem = id => async ({ store, dispatch }) => {
    dispatch({ type: types.delete })
    const access = store.selectAccessToken()
    const opts = tokenHeader(access)
    try {
      await axios.delete(`/api/${url}/${id}/`, opts)
      await store[`doFetch${bundleName}ModelList`]()
      store.doSetSnackbarInfo('Eliminado.')
    } catch (error) {
      store.doSetSnackbarFail('Error al eliminar.')
    }
  }
  const setCachedItems = cached => ({ dispatch }) => dispatch({ type: types.setCachedItems, payload: cached })
  const setSelectedItems = selectedItemsIds => ({ dispatch }) => dispatch({ type: types.setSelected, payload: selectedItemsIds })
  const setAllSelected = () => ({ dispatch }) => dispatch({ type: types.setAllSelected })
  const clearSelected = () => ({ dispatch }) => dispatch({ type: types.clearSelected })
  const setSearchValue = searchValue => async ({ store, dispatch }) => {
    dispatch({ type: types.setSearchValue, payload: searchValue })
    await store[`doFetch${bundleName}ModelList`]()
  }
  const clearSearchValue = () => ({ dispatch }) => dispatch({ type: types.clearSearchValue })
  const openDialog = id => ({ dispatch }) => dispatch({ type: types.openDialog, payload: id })
  const closeDialog = () => ({ dispatch }) => {
    dispatch({ type: types.closeDialog })
  }
  const actionCreatorsObject = {
    [`doFetch${bundleName}ModelList`]: fetchList,
    [`doCreate${bundleName}ModelItem`]: createItem,
    [`doUpdate${bundleName}ModelItem`]: updateItem,
    [`doSetCached${bundleName}ModelItems`]: setCachedItems,
    [`doSet${bundleName}SelectedItems`]: setSelectedItems,
    [`doSetAll${bundleName}ModelItemsSelected`]: setAllSelected,
    [`doClear${bundleName}ModelItemsSelected`]: clearSelected,
    [`doSet${bundleName}ModelSearchValue`]: setSearchValue,
    [`doClear${bundleName}ModelSearchValue`]: clearSearchValue,
    [`doOpen${bundleName}ModelDialog`]: openDialog,
    [`doClose${bundleName}ModelDialog`]: closeDialog,
  }
  if (allowDelete) {
    actionCreatorsObject[`doDelete${bundleName}ModelItem`] = deleteItem
  }
  return actionCreatorsObject
}

const getReactors = (bundleName) => {
  const fetchReactor = createSelector(
    `select${bundleName}ModelRaw`,
    'selectIsAuthenticated',
    'selectAppTime',
    'selectIsOnline',
    (raw, isAuthenticated, appTime, isOnline) => {
      if (!isAuthenticated) return
      if (raw.loading) return
      if (!isOnline) return
      let shouldFetch = false
      let timePassedLastFetch = null
      // if there are no entities, fetch
      if (raw.lastFetch) {
        timePassedLastFetch = appTime - raw.lastFetch
        if (raw.items.length === 0 && timePassedLastFetch > 60000) shouldFetch = true
        if (timePassedLastFetch > 60000 * 5) shouldFetch = true
      } else if (!raw.items.length) shouldFetch = true
      // if an error ocurred, wait 5s and retry
      if (raw.lastError) {
        const timePassed = appTime - raw.lastError
        if (timePassed > 10000) {
          shouldFetch = true
        }
      }
      // solve how to get out of next if when search fetch has happened already
      // if (raw.searchValue) {
      //   if (appTime - raw.lastSearchValueSet > 1000) shouldFetch = true
      // }
      if (shouldFetch) return { actionCreator: `doFetch${bundleName}ModelList` }
    }
  )
  // const searchFetchReactor = createSelector(
  //   `select${bundleName}ModelRaw`,
  //   'selectIsAuthenticated',
  //   'selectAppTime',
  //   (raw, isAuthenticated, appTime, isOnline) => {
  //     if (!isAuthenticated) return
  //     if (raw.loading) return
  //   },
  // )
  return {
    [`reactShouldFetch${bundleName}ModelItems`]: fetchReactor,
  }
}

export default config => {
  const {
    bundleName,
    camelCaseBundleName,
    verboseName,
    url,
    extraFields = {},
    customTypes = {},
    allowDelete = true,
  } = config
  const types = getTypes(bundleName, customTypes)
  const initialState = getInitialState(bundleName, extraFields, allowDelete)
  const reducer = getReducer(bundleName, initialState, types)
  const actionCreators = getActionCreators(bundleName, initialState, types, url, verboseName, allowDelete)
  const selectors = getSelectors(bundleName, camelCaseBundleName, initialState, types)
  const reactors = getReactors(bundleName)
  const init = store => {
    const cachedLocalStorage = localStorage.getItem(`cached${bundleName}ModelItems`)
    if (cachedLocalStorage === null) return
    const cachedEntities = JSON.parse(cachedLocalStorage)
    store[`doSetCached${bundleName}ModelItems`](cachedEntities)
    const isAuth = store.selectIsAuthenticated()
    if (isAuth) store[`doFetch${bundleName}ModelList`]()
  }
  return {
    name: camelCaseBundleName,
    reducer,
    ...selectors,
    ...actionCreators,
    ...reactors,
    init,
  }
}
